
export enum UserRole {
  STUDENT = 'STUDENT',
  AGENT = 'AGENT',
  LANDLORD = 'LANDLORD',
  ADMIN = 'ADMIN'
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  status: 'PENDING' | 'APPROVED' | 'ACTIVE' | 'REJECTED';
  phoneNumber?: string;
  institution?: string;
  agencyName?: string;
  licenseNumber?: string;
  propertyCount?: string;
  referralCode?: string; // Unique code for the user
  referralBalance?: number; // Current redeemable amount
  lifetimeEarnings?: number; // Total amount earned
  referralCount?: number; // Total number of successful referrals
  isTrusted?: boolean; // If true, agent/landlord listings bypass approval
  joinedDate?: string; // ISO Date string of registration
  lastEmailChange?: string; // ISO Date string of last email change
  deletedAt?: string;
  deletedBy?: string;
}

export interface Landlord {
  id: string;
  agentId?: string;
  fullName: string;
  phoneNumber: string;
  email: string;
  physicalAddress?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface AuditLog {
  id: string;
  userId?: string;
  action: string;
  targetId?: string;
  targetType?: string;
  payload?: any;
  createdAt: string;
}

export interface InternalNote {
  id: string;
  userId?: string;
  targetId: string;
  targetType: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface BedDefinition {
  id: string;
  name: string;
  price: number;
  available: boolean;
}

export interface RoomDefinition {
  id: string;
  name: string;
  price: number;
  description: string;
  available: boolean;
  pricingType: 'per_room' | 'per_bed';
  beds?: BedDefinition[];
}

export interface Property {
  id: string;
  title: string;
  slug?: string; // SEO friendly URL slug
  location: string;
  locationName?: string; // New: Descriptive name for the spot/building
  latitude?: number; // New: For Geo-location
  longitude?: number; // New: For Geo-location
  price: number; // Base price or range start
  type: 'Apartment' | 'House' | 'Studio' | 'Dorm';
  bedrooms: number;
  bathrooms: number;
  image: string; // Primary image (legacy support)
  images?: string[]; // Multiple images
  agentId: string; // or landlordId
  status: 'AVAILABLE' | 'RENTED' | 'PENDING';
  description: string;
  amenities: string[];
  rooms?: RoomDefinition[]; // New: Detailed room configuration
  beds?: BedDefinition[]; // Explicit bed management
  landlordId?: string;
  coAgentId?: string; // New: For co-management / secondary agent
  deletedAt?: string;
  deletedBy?: string;
}

export interface Booking {
  id: string;
  propertyId: string;
  studentId: string;
  roomId?: string; // Specific room booked
  bedId?: string; // Specific bed booked
  status: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED';
  date: string;
  referralCode?: string; // Code used when making this booking
  deletedAt?: string;
  deletedBy?: string;
}

export interface Notification {
  id: string;
  userId: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  date: string;
  read: boolean;
}

export interface Message {
  id: string;
  senderId: string;
  receiverId: string;
  propertyId?: string;
  content: string;
  isRead: boolean;
  createdAt: string;
  senderName?: string; // Hydrated for UI
}

export interface Payment {
  id: string;
  bookingId?: string;
  studentId: string;
  amount: number;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  title: string;
  dueDate: string;
  paidAt?: string;
}

export interface SavedProperty {
  userId: string;
  propertyId: string;
  createdAt: string;
}

export interface ReferralCampaign {
  id: string;
  name: string;
  benefit: string; // "10% off" (For Friend)
  rewardAmount: number; // 50 (For Referrer)
  thresholdAmount: number; // 300 (Redemption Limit)
  isActive: boolean;
  startDate: string;
  endDate?: string;
}

export interface WithdrawalRequest {
  id: string;
  userId: string;
  amount: number;
  status: 'PENDING' | 'PAID' | 'REJECTED';
  createdAt: string;
  bankName: string;
  accountNumber: string;
  accountHolderName: string;
  branchCode: string;
  idNumber: string;
  email: string;
}

export interface StatCardProps {
  label: string;
  value: string | number;
  icon: any;
  trend?: string;
  color?: string;
}

export type ApplicationType = 'NSFAS' | 'CASH' | 'BURSARY';

export type FormFieldType = 'text' | 'textarea' | 'number' | 'select' | 'multiselect' | 'date' | 'file' | 'checkbox';

export interface FormField {
  id: string;
  type: FormFieldType;
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[]; // For select/multiselect
  accept?: string; // For file inputs (e.g. .pdf, .jpg)
}

export interface ApplicationForm {
  id: string;
  agentId: string;
  title: string;
  type: ApplicationType;
  description?: string;
  isActive: boolean;
  createdAt: string;
  fields: FormField[];
}